import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

export type TipoApunte = 'ingreso' | 'gasto';
export type EstadoApunte =
  | 'procesando'
  | 'pending_review'
  | 'validation_failed'
  | 'confirmed'
  | 'error';

/** Un apunte del libro. Nace de una factura leída por IA (`source='ia'`) o de un
 *  alta manual (`source='manual'`). Los importes llegan como número (el backend
 *  guarda Decimal y el encoder los pasa a float). `anio`/`trimestre` vienen
 *  denormalizados. Nunca trae IBAN ni datos bancarios. */
export interface Apunte {
  doc_id: string;
  tipo?: TipoApunte;
  categoria?: string;
  concepto?: string;
  contraparte?: string;
  fecha?: string;
  anio?: string;
  trimestre?: string;
  base_imponible?: number;
  iva_pct?: number;
  iva_amount?: number;
  /** Retención de IRPF (facturas de autónomos), en positivo. */
  irpf_pct?: number;
  irpf_amount?: number;
  /** Lo que figura en la factura: base + IVA − retención. */
  total?: number;
  estado: EstadoApunte;
  source?: 'ia' | 'manual';
  s3_key?: string;
  created_at?: string;
  updated_at?: string;
}

interface LibroResponse {
  items: Apunte[];
  categorias_sugeridas: Record<TipoApunte, string[]>;
}

/** El libro. `anio` opcional filtra por año (anio-index). */
export function useAccounting(anio?: string) {
  return useQuery<LibroResponse>({
    queryKey: ['accounting', anio ?? 'todo'],
    queryFn: async () =>
      (await api.get('/crm/accounting', { params: anio ? { anio } : {} })).data,
  });
}

/** Sube una factura (foto/PDF): pide URL firmada, hace PUT a S3 y devuelve el
 *  doc_id. El worker (Bedrock) rellena la fila de forma asíncrona; el poll de
 *  `useApunteStatus` espera a que deje de estar 'procesando'. */
export function useUploadFactura() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const { data } = await api.post('/crm/accounting/uploads', {
        content_type: file.type,
      });
      await fetch(data.upload_url, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });
      return data.doc_id as string;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['accounting'] }),
  });
}

/** Poll de la ingesta. Sondea cada 2 s mientras la fila esté 'procesando'. */
export function useApunteStatus(docId: string | null) {
  return useQuery<Apunte>({
    queryKey: ['apunte', docId],
    queryFn: async () => (await api.get(`/crm/accounting/${docId}/status`)).data,
    enabled: Boolean(docId),
    refetchInterval: (query) =>
      query.state.data?.estado === 'procesando' ? 2000 : false,
  });
}

/** Alta manual (POST) o edición/confirmación (PATCH) de un apunte. */
export function useSaveApunte() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (a: Partial<Apunte> & { doc_id?: string }) => {
      const { doc_id, ...body } = a;
      const res = doc_id
        ? await api.patch(`/crm/accounting/${doc_id}`, body)
        : await api.post('/crm/accounting', body);
      return res.data as Apunte;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['accounting'] }),
  });
}

export function useDeleteApunte() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (docId: string) =>
      (await api.delete(`/crm/accounting/${docId}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['accounting'] }),
  });
}

/** Categorías ya usadas, derivadas del libro (patrón useCategorias de maman-tax:
 *  cero llamadas extra, se limpia solo). Se combinan con las sugeridas del tipo. */
export function categoriasUsadas(items: Apunte[] | undefined): string[] {
  const seen = new Set<string>();
  for (const a of items ?? []) if (a.categoria) seen.add(a.categoria);
  return Array.from(seen).sort((x, y) => x.localeCompare(y, 'es'));
}
